import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Edit2 } from 'lucide-react';
import { CardSectionProps } from '../../../types/compositeCards';
import { PrimeArtifact, PrimeProcess } from '../../../types/primeModel';
import { PrimeArtifactService } from '../../../services/primeArtifactService';
import styles from './PrimeProcessesContextCard.module.css';

export const PrimeProcessesContextCard: React.FC<CardSectionProps> = ({ card, onAction }) => {
  const [processes, setProcesses] = useState<PrimeProcess[]>([]);
  const [loading, setLoading] = useState(false);
  const [isApproved, setIsApproved] = useState(false);

  const data = card.data as any;
  const primeArtifact: PrimeArtifact = data.primeArtifact || data.artifact;
  const isArchitect = card.context.uiRole === 'architect';

  useEffect(() => {
    const loadProcesses = async () => {
      if (!primeArtifact?.id) return;
      setLoading(true);
      try {
        const artifact = await PrimeArtifactService.getById(primeArtifact.id);
        setProcesses(artifact.processes || []);
        setIsApproved(artifact.processes_approved || false);
      } catch (error) {
        console.error('Failed to load prime processes:', error);
      } finally {
        setLoading(false);
      }
    };
    void loadProcesses();
  }, [primeArtifact?.id]);

  const handleToggleApproval = async () => {
    if (!primeArtifact?.id) return;
    try {
      if (isApproved) {
        await PrimeArtifactService.rejectProcesses(primeArtifact.id);
        setIsApproved(false);
      } else {
        await PrimeArtifactService.approveProcesses(primeArtifact.id);
        setIsApproved(true);
      }
    } catch (error) {
      console.error('Failed to toggle processes approval:', error);
    }
  };

  if (loading) return <div className={styles.primeProcessesLoading}>Loading processes...</div>;

  return (
    <div className={styles.primeProcessesContextCard}>
      <div className={styles.primeProcessesHeader}>
        <h3>Prime Processes</h3>
        {isArchitect && (
          <button className={`${styles.approvalToggle} ${isApproved ? styles.approved : styles.notApproved}`} onClick={handleToggleApproval}>
            {isApproved ? <CheckCircle size={16} /> : <XCircle size={16} />}
            {isApproved ? 'Approved' : 'Not Approved'}
          </button>
        )}
      </div>
      {processes.length === 0 ? (
        <div className={styles.noProcessesMessage}><p>No processes defined yet</p></div>
      ) : (
        <div className={styles.processesList}>
          {processes.map((process) => (
            <div key={process.id} className={styles.processItem}>
              <div className={styles.processName}>{process.name}</div>
              {process.description && <div className={styles.processDescription}>{process.description}</div>}
              <div className={styles.processTypeBadge}>{process.process_type}</div>
            </div>
          ))}
        </div>
      )}
      {isArchitect && (
        <div className={styles.primeProcessesFooter}>
          <button className={styles.editProcessesButton} onClick={() => onAction?.('edit-processes', { primeArtifact })}>
            <Edit2 size={14} />
            Edit Processes
          </button>
        </div>
      )}
    </div>
  );
};

